import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { resolveGoodsImage } from "@laftel-ip-originals/notion/locale";
import { useI18n } from "~/i18n";
import { Header } from "~/components/header";
import { useGoods } from "~/hooks/use-goods";
import { getOriginalBySlug } from "~/data/ip";
import { useLenis } from "~/hooks/use-lenis";
import { useRevealOnScroll } from "~/hooks/use-reveal-on-scroll";
import {
  GoodsTitle,
  formatGoodsTitle,
  type GoodsTitleParts,
} from "~/components/sections/goods-title";

export function GoodsDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, t } = useI18n();
  const { data, isLoading } = useGoods(slug);
  const original = slug ? getOriginalBySlug(slug) : undefined;

  useLenis(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const detailImage = resolveGoodsImage(data?.descriptionImage, locale);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    setImgLoaded(false);
  }, [detailImage]);

  useRevealOnScroll(containerRef, {
    rootMargin: "200px 0px -40px 0px",
    startDelay: 50,
  });

  if (!data && !isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-[var(--ip-text-muted)]">
        Not Found
      </div>
    );
  }

  const titleParts: GoodsTitleParts | null = original
    ? {
        workName: original.goodsTitle?.[locale] ?? original.title[locale],
        subtitle: t.md.subtitle,
        locale,
      }
    : null;
  const titleText = titleParts ? formatGoodsTitle(titleParts) : "";

  return (
    <div className="min-h-dvh bg-white">
      <Header forceScrolled />
      <div
        ref={containerRef}
        className="mx-auto max-w-[1100px] px-[clamp(24px,5vw,80px)] pt-[64px] pb-[80px] max-md:pt-[32px]"
      >
        {/* Back */}
        <button
          onClick={() => {
            const from = (location.state as { from?: string })?.from;
            navigate(`/${slug}${from === "goods" ? "#goods" : ""}`);
          }}
          className="reveal-fade goods-store-card mb-8 max-md:mb-4 inline-flex items-center gap-2 rounded-[8px] px-4 py-2 text-sm text-[var(--ip-text)] transition-all duration-300 hover:bg-[#fcfcfc]"
        >
          <span>←</span>
          <span>{t.md.goBack}</span>
        </button>

        {/* Title */}
        <h1
          className="reveal-fade mb-10 max-md:mb-5 text-[28px] font-bold tracking-[0.02em] text-[var(--ip-text)]"
          style={{ transitionDelay: "0.1s" }}
        >
          {titleParts && <GoodsTitle parts={titleParts} />}
        </h1>

        {/* Content: image + stores */}
        <div className="flex gap-8 max-md:flex-col">
          {/* Detail Image */}
          <div className="reveal-fade flex-1 min-w-0" style={{ transitionDelay: "0.2s" }}>
            <div className="w-full rounded-[16px] max-md:rounded-[12px] bg-[var(--ip-surface)] overflow-hidden">
              {isLoading && !data ? (
                <div className="w-full aspect-[3/4] skeleton" />
              ) : detailImage ? (
                <div className="relative">
                  {!imgLoaded && <div className="w-full aspect-[3/4] skeleton" />}
                  <img
                    src={detailImage}
                    alt={titleText}
                    className={`w-full block transition-opacity duration-300 ${imgLoaded ? "opacity-100" : "opacity-0 absolute top-0 left-0"}`}
                    onLoad={() => setImgLoaded(true)}
                    ref={(el) => {
                      if (el?.complete && el.naturalWidth > 0) setImgLoaded(true);
                    }}
                  />
                </div>
              ) : (
                <div className="w-full aspect-[3/4] flex items-center justify-center text-[var(--ip-text-muted)] text-sm">
                  COMING SOON
                </div>
              )}
            </div>
          </div>

          {/* Stores — desktop only */}
          {data?.stores && data.stores.length > 0 && (
            <div className="w-[280px] shrink-0 max-md:w-full">
              <div className="sticky top-[100px] max-md:static flex flex-col gap-4 max-md:mt-4">
                {data.stores.map((store, i) => (
                  <a
                    key={i}
                    href={store.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="goods-store-card group flex flex-col gap-3 rounded-[12px] bg-white px-5 py-4 opacity-0 animate-[fadeSlideIn_0.6s_ease_both] transition-[background-color] duration-300 hover:bg-[#fcfcfc] text-left cursor-pointer"
                    style={{ animationDelay: `${0.3 + i * 0.1}s` }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {store.icon ? (
                          <img
                            src={store.icon}
                            alt=""
                            className="w-5 h-5 rounded-[3px] shrink-0 object-contain"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-[3px] bg-[var(--ip-surface)] shrink-0" />
                        )}
                        <span className="text-[15px] font-bold text-[var(--ip-text)]">
                          {store.name}
                        </span>
                      </div>
                      <span className="text-sm text-[#aaa] transition-transform group-hover:translate-x-1">
                        →
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
